"use client";

import { useCallback, useEffect, useRef } from "react";
import { useTrackedCall } from "@/hooks/use-call-error-state";
import type { ErrorRecovery } from "@/lib/handle-error";

export interface AsyncCallState {
  error: string | null;
  clearError: () => void;
  isLoading: boolean;
  recovery: ErrorRecovery;
  reportable: boolean;
  errorEventId?: string;
  retry?: () => void;
}

interface UseAsyncCallWithStateOptions<TReturn> {
  retryable?: boolean;
  onSuccess?: (result: TReturn) => void;
  onError?: (errorMessage: string) => void;
  onSettled?: () => void;
}

export interface UseAsyncCallWithStateReturn<
  TArgs extends unknown[],
  TReturn,
> extends AsyncCallState {
  execute: (...args: TArgs) => Promise<TReturn | undefined>;
}

export function useAsyncCallWithState<TArgs extends unknown[], TReturn>(
  callable: (...args: TArgs) => Promise<TReturn>,
  options?: UseAsyncCallWithStateOptions<TReturn>,
): UseAsyncCallWithStateReturn<TArgs, TReturn> {
  const { run, ...state } = useTrackedCall({
    retryable: options?.retryable,
  });
  const optionsRef = useRef(options);
  const callableRef = useRef(callable);

  useEffect(() => {
    callableRef.current = callable;
    optionsRef.current = options;
  });

  const execute = useCallback(
    (...args: TArgs): Promise<TReturn | undefined> =>
      run(() => callableRef.current(...args), {
        onSuccess: (result) => optionsRef.current?.onSuccess?.(result),
        onError: (errorMessage) => optionsRef.current?.onError?.(errorMessage),
        onSettled: () => optionsRef.current?.onSettled?.(),
      }),
    [run],
  );

  return { execute, ...state };
}
